// Step 2: BrandApplication2 for brand signup wizard
// Collects account owner information: First Name, Last Name, Email, Phone, Password, Confirm Password
// Simplified 2-step wizard - Step 2 of 2

import React, { useState, useEffect } from 'react';
import { validatePassword, validatePasswordMatch } from '../../../../utils/passwordValidation.js';
import { validatePhone } from '../../../../utils/phoneValidation.js';

const BrandApplication2 = ({ data, onDataChange, onNext, user }) => {
  // Editable copy constants
  const COPY = {
    title: 'Account Information',
    subtitle: 'Create your account',
    continueButton: 'Continue'
  };

  const [formData, setFormData] = useState({
    account: {
      email: data?.account?.email || '',
      password: data?.account?.password || '',
      confirmPassword: data?.account?.confirmPassword || '',
      phone: data?.account?.phone || '',
      firstName: data?.account?.firstName || '',
      lastName: data?.account?.lastName || ''
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Update parent component when form data changes
  useEffect(() => {
    onDataChange(formData);
  }, [formData, onDataChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested fields
    if (name.startsWith('account.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        account: {
          ...prev.account,
          [field]: value
        }
      }));
      
      // Clear phone error when user types in phone field
      if (field === 'phone') {
        setPhoneError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Clear error when user starts typing
    if (error) setError('');
  };

  // Validation function
  const validate = async (stepData) => {
    console.log('🔍 BrandApplication2 validation called with data:', stepData);
    const errors = [];
    const account = stepData.account || {};
    
    // Account validation
    if (!account.email?.trim()) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(account.email)) {
      errors.push('Please enter a valid email address');
    }
    
    // Phone number validation
    if (!account.phone?.trim()) {
      errors.push('Phone number is required');
    } else {
      const phoneValidation = validatePhone(account.phone);
      if (!phoneValidation.valid) {
        errors.push(phoneValidation.error);
      }
    }
    
    if (!account.firstName?.trim()) {
      errors.push('First name is required');
    }
    
    if (!account.lastName?.trim()) {
      errors.push('Last name is required');
    }
    
    // Password validation using utility
    if (!account.password?.trim()) {
      errors.push('Password is required');
    } else {
      const passwordValidation = validatePassword(account.password);
      if (!passwordValidation.valid) {
        errors.push(...passwordValidation.errors);
      }
    }
    
    // Password match validation
    if (account.password) {
      const matchValidation = validatePasswordMatch(account.password, account.confirmPassword);
      if (!matchValidation.valid) {
        errors.push(matchValidation.error);
      }
    } else if (!account.confirmPassword?.trim()) {
      errors.push('Please confirm your password');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Attach validate function to component
  BrandApplication2.validate = validate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validation = await validate(formData);
    if (!validation.valid) {
      setError(validation.errors.join(', '));
      
      // Set phone error if phone validation failed
      if (formData.account.phone) {
        const phoneValidation = validatePhone(formData.account.phone);
        if (!phoneValidation.valid) {
          setPhoneError(phoneValidation.error);
        }
      } else if (!formData.account.phone?.trim()) {
        setPhoneError('Phone number is required');
      }
      
      return;
    }

    setIsSubmitting(true);
    setError('');
    setPhoneError(''); // Clear phone error on successful validation

    try {
      // Update wizard state with Step 2 data
      const step2Data = {
        account: formData.account
      };
      onDataChange(step2Data);

      // Advance to next step (which is the submission step)
      onNext();

    } catch (error) {
      console.error('Account setup error:', error);
      setError('Failed to process step. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Error Display */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Account Information Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Account Information</h3>
        <p className="text-sm text-gray-600 mb-6">This will be the primary contact person for your brand account.</p>
        
        <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
          {/* First Name */}
          <div>
            <label htmlFor="account.firstName" className="form-label">
              First Name *
            </label>
            <input
              id="account.firstName"
              name="account.firstName"
              type="text"
              value={formData.account.firstName}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your first name"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="account.lastName" className="form-label">
              Last Name *
            </label>
            <input
              id="account.lastName"
              name="account.lastName"
              type="text"
              value={formData.account.lastName}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your last name"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="account.email" className="form-label">
              Email Address *
            </label>
            <input
              id="account.email"
              name="account.email"
              type="email"
              value={formData.account.email}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your email"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="account.phone" className="form-label">
              Phone Number *
            </label>
            <input
              id="account.phone"
              name="account.phone"
              type="tel"
              value={formData.account.phone}
              onChange={handleChange}
              onBlur={(e) => {
                // Validate on blur
                const phoneValidation = validatePhone(e.target.value);
                if (!phoneValidation.valid) {
                  setPhoneError(phoneValidation.error);
                } else {
                  setPhoneError('');
                }
              }}
              className={`form-input ${phoneError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
              placeholder="(555) 123-4567"
              required
            />
            {phoneError && (
              <p className="mt-1 text-xs text-red-500">
                {phoneError}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="account.password" className="form-label">
              Password *
            </label>
            <input
              id="account.password"
              name="account.password"
              type="password"
              value={formData.account.password}
              onChange={handleChange}
              className="form-input"
              placeholder="Create a password (min 9 characters, 3 of 4: lowercase, uppercase, number, special)"
              required
              minLength={9}
            />
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="account.confirmPassword" className="form-label">
              Confirm Password *
            </label>
            <input
              id="account.confirmPassword"
              name="account.confirmPassword"
              type="password"
              value={formData.account.confirmPassword}
              onChange={handleChange}
              className="form-input"
              placeholder="Confirm your password"
              required
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {isSubmitting ? 'Processing...' : COPY.continueButton}
        </button>
      </div>
    </form>
  );
};

export default BrandApplication2;
